import {Component, inject} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialogRef } from '@angular/material/dialog';
import { CardVerificationResponse } from '../../core/interfaces/card-verification';
import { CardVerificationService } from '../../core/services/card-verification.service';
import { TranslateService } from '@ngx-translate/core';

type VerificationResult = 'valid' | 'inactive' | 'not-found' | null;

@Component({
  selector: 'app-card-validity-dialog',
  templateUrl: './card-validity-dialog.component.html',
  styleUrls: ['./card-validity-dialog.component.scss'],
  standalone: false
})
export class CardValidityDialogComponent {
  private readonly translate = inject(TranslateService);

  cardNumber = '';
  verificationResult: VerificationResult = null;
  validFrom = '';
  validUntil = '';
  verificationMessage = '';
  isChecking = false;

  constructor(
    private dialogRef: MatDialogRef<CardValidityDialogComponent>,
    private cardVerificationService: CardVerificationService
  ) {}

  get isCardNumberComplete(): boolean {
    return this.getDigits(this.cardNumber).length === 9;
  }

  onCardNumberChange(value: string): void {
    this.cardNumber = this.formatCardNumber(value);
    this.verificationResult = null;
    this.validFrom = '';
    this.validUntil = '';
    this.verificationMessage = '';
  }

  checkMembershipStatus(): void {
    if (!this.isCardNumberComplete || this.isChecking) {
      return;
    }

    this.isChecking = true;
    this.cardVerificationService.verifyCard(Number(this.getDigits(this.cardNumber))).subscribe({
      next: (response) => {
        this.isChecking = false;
        this.handleVerificationResponse(response);
      },
      error: (error: HttpErrorResponse) => {
        this.isChecking = false;
        this.verificationResult = this.getErrorResult(error);
        this.verificationMessage = this.getErrorMessage(error);
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  get resultTitle(): string {
    if (this.verificationResult === 'valid') {
      return this.translate.instant('dashboard.verify_card.valid_card_title');
    }
    return this.verificationResult === 'inactive'
      ? this.translate.instant('dashboard.verify_card.invalid_card_title')
      : this.translate.instant('dashboard.verify_card.card_not_found_title');
  }

  get resultMessage(): string {
    if (this.verificationMessage) {
      return this.verificationMessage;
    }

    if (this.verificationResult === 'valid') {
      return this.getValidCardMessage();
    }
    if (this.verificationResult === 'inactive') {
      return this.translate.instant('dashboard.verify_card.validation.error_message');
    }
    return this.translate.instant('dashboard.verify_card.validation.not_found_message');
  }

  private handleVerificationResponse(response: CardVerificationResponse): void {
    const result = response.data ?? response;
    this.verificationResult = this.isValidCard(result) ? 'valid' : this.getResponseResult(result);
    this.verificationMessage = result.message ?? '';
    this.validFrom = this.getDateValue(result, ['valid_from', 'start_date']);
    this.validUntil = this.getDateValue(result, ['valid_until', 'end_date']);
  }

  private isValidCard(response: CardVerificationResponse): boolean {
    return response.valid === true
      || response.status?.toLowerCase() === 'valid'
      || response.status?.toLowerCase() === 'active';
  }

  private getResponseResult(response: CardVerificationResponse): Exclude<VerificationResult, 'valid' | null> {
    const status = response.status?.toLowerCase();
    return status === 'not_found' || status === 'error' ? 'not-found' : 'inactive';
  }

  private getErrorResult(error: HttpErrorResponse): Exclude<VerificationResult, 'valid' | null> {
    const errorBody = error.error as Partial<CardVerificationResponse> | null;
    if (error.status === 404 || error.status === 422  || errorBody?.error_code === 'VALIDATION_ERROR') {
      return 'not-found';
    }

    const status = errorBody?.status?.toLowerCase();
    return status === 'not_found' || status === 'error' ? 'not-found' : 'inactive';
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    const errorBody = error.error as Partial<CardVerificationResponse> | null;
    return errorBody?.errors?.card_number?.[0] ?? '';
  }

  private getValidCardMessage(): string {
    if (this.validFrom && this.validUntil) {
      return this.translate.instant(
        'dashboard.verify_card.validation.success_message',
        {valid_from_date: this.validFrom, valid_until: this.validUntil}
      );
    }
    return this.translate.instant('dashboard.verify_card.validation.success_message_alternative');
  }

  private getDateValue(response: CardVerificationResponse, keys: Array<'valid_from' | 'valid_until' | 'start_date' | 'end_date'>): string {
    const dateValue = keys.map(key => response[key]).find(value => typeof value === 'string');
    return dateValue ? this.formatDate(dateValue) : '';
  }

  private formatCardNumber(value: string): string {
    const digits = this.getDigits(value).slice(0, 9);
    return digits.replace(/(\d{3})(?=\d)/g, '$1-');
  }

  private getDigits(value: string): string {
    return value.replace(/\D/g, '');
  }

  private formatDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  }
}
